import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { Link, useNavigate } from "react-router-dom";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import RenderCategoryRow from "./renderRowCategory";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";

import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import "./manageCategories.scss";

const CATEGORY_SORT_FIELDS = {
  name: "RootCat_Name",
  sortOrder: "rootCat_Orderby",
  status: "RootCat_Publish",
  department: "DepartmentName",
  categoryType: "ReportingCategoryName",
};

const CATEGORY_SORT_LABELS = {
  [CATEGORY_SORT_FIELDS.name]: "Name",
  [CATEGORY_SORT_FIELDS.sortOrder]: "Order By",
  [CATEGORY_SORT_FIELDS.status]: "Status",
  [CATEGORY_SORT_FIELDS.department]: "Department",
  [CATEGORY_SORT_FIELDS.categoryType]: "Category Type",
};

const ManageCategories = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "categories";
  const defaultVisibleColumns = {
    status: true,
    department: true,
    categoryType: true,
    products: false,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "rootCat_Orderby", label: "Order By" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialCategoriesLoadComplete, setInitialCategoriesLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const categoriesFetchSeqRef = useRef(0);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: CATEGORY_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  // API returns full tree; pagination is client-side on top-level roots only.
  const fetchCategoriesData = useCallback(async () => {
    const fetchSeq = ++categoriesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== categoriesFetchSeqRef.current) return;

      const categoryData = response.data.data || response.data;
      const roots = Array.isArray(categoryData) ? categoryData : [];
      setCategories(roots);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, roots.length);
      }
    } catch (err) {
      if (fetchSeq !== categoriesFetchSeqRef.current) return;
      setCategories([]);
      showErrorToast("Failed to load categories");
    } finally {
      if (fetchSeq === categoriesFetchSeqRef.current) {
        setLoading(false);
        setInitialCategoriesLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID]);

  const totalRecords = categories.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedCategories = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return categories.slice(start, start + size);
  }, [categories, pageNumber, pageSize]);

  const findAndDeleteCategory = (categoriesList, categoryId) => {
    return categoriesList
      .map((category) => {
        if (category.rootCat_ID === categoryId) {
          return null;
        }
        if (category.subCategoriesList && category.subCategoriesList.length > 0) {
          return {
            ...category,
            subCategoriesList: findAndDeleteCategory(
              category.subCategoriesList,
              categoryId
            ).filter(Boolean),
          };
        }
        return category;
      })
      .filter(Boolean);
  };

  const DeleteCategory = async (category) => {
    const firstConfirm = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${category.rootCat_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!firstConfirm) return;

    const hasSubcategories =
      category.subCategoriesList && category.subCategoriesList.length > 0;

    if (hasSubcategories) {
      const secondConfirm = await swal({
        title: "Warning: Subcategories Detected",
        text: `Deleting ${category.rootCat_Name} will also delete its subcategories. Are you sure you want to proceed?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!secondConfirm) return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_ROOT_CATEGORY, {
        params: {
          CompID,
          RootCat_ID: category.rootCat_ID,
        },
      });

      if (response.data.status) {
        setCategories((prevCategories) => {
          const next = findAndDeleteCategory(prevCategories, category.rootCat_ID);
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast(`${category.rootCat_Name} has been deleted.`);
      } else {
        showErrorToast(response.data.message || "Failed to delete category.");
      }

      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (
        e.reason &&
        e.reason.message &&
        e.reason.message.includes("ResizeObserver")
      ) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (
        args[0] &&
        typeof args[0] === "string" &&
        args[0].includes("ResizeObserver")
      ) {
        return;
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      window.removeEventListener("error", handleResizeObserverError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
      console.error = originalConsoleError;
    };
  }, []);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(categoriesFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchCategoriesData(), [fetchCategoriesData]);

  useEffect(() => {
    if (!initialCategoriesLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialCategoriesLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar =
    initialCategoriesLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No categories match your search "${search.trim()}". Try different keywords or clear filters to see all categories.`;
    }
    return "There are no categories to show right now. Add a new category or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || categories.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No categories found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Categories</li>
          </ol>
          <h1 className="page-header mb-0">Manage Categories</h1>
        </div>

        <div className="ms-auto">
          <Link to="/categories/addCategory">
            <button
              type="button"
              className="btn px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
              Add Category
            </button>
          </Link>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialCategoriesLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <AddButton
            onClick={() => navigate("/categories/addCategory")}
            title="Add Category"
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Categories"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedCategories.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover manage-categories-table"
                      style={{
                        "--modern-grid-sort-active": themeColor,
                      }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" />
                          <SortableColumnHeader
                            sortField={CATEGORY_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Category
                          </SortableColumnHeader>
                          {visibleColumns.department && (
                            <SortableColumnHeader
                              sortField={CATEGORY_SORT_FIELDS.department}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Department
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.categoryType && (
                            <SortableColumnHeader
                              sortField={CATEGORY_SORT_FIELDS.categoryType}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Category Type
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.products && (
                            <th className="pt-2 pb-2 text-center">Products</th>
                          )}
                          {visibleColumns.status && (
                            <SortableColumnHeader
                              sortField={CATEGORY_SORT_FIELDS.status}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Status
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCategories.map((category, index) => (
                          <RenderCategoryRow
                            key={category.rootCat_ID}
                            category={category}
                            index={index}
                            categories={categories}
                            setCategories={setCategories}
                            visibleColumns={visibleColumns}
                            DeleteCategory={DeleteCategory}
                            themeColor={themeColor}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
      </div>
    </ErrorBoundary>
  );
};

export default ManageCategories;
